import "moment/locale/it";

import * as React from "react";

import { Admin, Loading, Resource } from "react-admin";
import {
  WorkflowProvider,
  createI18nProvider,
  useAuthProvider,
  useDataProvider,
  useI18nCatcher,
  useI18nLanguages,
} from "ra-friendsofbabba";

import { API_URL } from "./config";
import Layout from "./component/Layout";
import LoginPage from "./component/page/LoginPage";
import ProfilePage from "./component/page/ProfilePage";
import { Route } from "react-router-dom";
import docCategories from "./doc-categories";
import docFiles from "./doc-files";
import executiveProjectCollaborations from "./executive-project-collaborations";
import executiveProjectDocs from "./executive-project-docs";
import executiveProjectEquipments from "./executive-project-equipments";
import executiveProjectFinancialResources from "./executive-project-financial-resources";
import executiveProjectWorkPackages from "./executive-project-work-packages";
import executiveProjects from "./executive-projects";
import intermediateTechnicalReportDocs from "./intermediate-technical-report-docs";
import intermediateTechnicalReports from "./intermediate-technical-reports";
import itMessages from "ra-language-italian";
import languageMessages from "./language-messages";
import monitoring from "./monitoring";
import monitoringExperts from "./monitoring-experts";
import notifications from "./notifications";
import technicalReportActivities from "./technical-report-activities";
import technicalReportCollaborations from "./technical-report-collaborations";
import technicalReportConferences from "./technical-report-conferences";
import technicalReportDeliverableResults from "./technical-report-deliverable-results";
import technicalReportDeliverables from "./technical-report-deliverables";
import technicalReportEnhancementActions from "./technical-report-enhancement-actions";
import technicalReportEvalReplies from "./technical-report-eval-replies";
import technicalReportEvals from "./technical-report-evals";
import technicalReportFinalOutputs from "./technical-report-final-outputs";
import technicalReportGroups from "./technical-report-groups";
import technicalReportPatents from "./technical-report-patents";
import technicalReportProjects from "./technical-report-projects";
import technicalReportReplies from "./technical-report-replies";
import technicalReportScientificProductions from "./technical-report-scientific-productions";
import technicalReports from "./technical-reports";
import theme from "./theme";
import universityDepartments from "./university-departments";
import users from "./users";

const App = () => {
  const apiUrl = API_URL;
  const dataProvider = useDataProvider({
    apiUrl,
    fileFields: [
      "file",
      "intermediate_technical_report_docs",
      "docs",
      "verification_doc_media",
      "doc_media",
      "signed_doc_media",
      "attachment_media",
    ],
  });
  const authProvider = useAuthProvider({ apiUrl });
  const languages = useI18nLanguages({ apiUrl });
  useI18nCatcher({ apiUrl });

  if (languages?.loading) {
    return (
      <Loading loadingPrimary="Loading" loadingSecondary="Please wait..." />
    );
  }

  return (
    <WorkflowProvider apiUrl={apiUrl}>
      <Admin
        title="REFIN"
        theme={theme}
        dataProvider={dataProvider}
        authProvider={authProvider}
        i18nProvider={createI18nProvider({
          languages: {
            ...languages?.data,
            it: {
              ...languages?.data?.it,
              ...itMessages,
            },
          },
          locale: "it",
        })}
        loginPage={LoginPage}
        layout={Layout}
        customRoutes={[
          <Route
            key="user-profile"
            path="/user-profile"
            component={ProfilePage}
          />,
        ]}
      >
        <Resource name="notifications" {...notifications} />

        <Resource name="executive-projects" {...executiveProjects} />
        <Resource
          name="executive-project-work-packages"
          {...executiveProjectWorkPackages}
        />
        <Resource name="executive-project-docs" {...executiveProjectDocs} />
        <Resource
          name="executive-project-collaborations"
          {...executiveProjectCollaborations}
        />
        <Resource
          name="executive-project-equipments"
          {...executiveProjectEquipments}
        />
        <Resource name="executive-project-work-package-activities" />
        <Resource name="executive-project-work-package-deliverables" />
        <Resource
          name="executive-project-collaborations"
          {...executiveProjectCollaborations}
        />
        <Resource
          name="executive-project-financial-resources"
          {...executiveProjectFinancialResources}
        />
        <Resource
          name="executive-project-equipments"
          {...executiveProjectEquipments}
        />
        <Resource
          name="intermediate-technical-reports"
          {...intermediateTechnicalReports}
        />
        <Resource
          name="intermediate-technical-report-docs"
          {...intermediateTechnicalReportDocs}
        />
        <Resource name="project-proposals" />
        <Resource name="partner-types" />
        <Resource name="collaboration-types" />
        <Resource name="collab-partner-types" />
        <Resource name="doc-categories" {...docCategories} />
        <Resource name="doc-subcategories" />
        <Resource name="scientific-disciplinary-sectors" />
        <Resource name="roles" />
        <Resource name="languages" />
        <Resource name="profile" />
        <Resource name="universities" />
        <Resource name="university-departments" {...universityDepartments} />

        <Resource name="formalization-types" />
        <Resource name="result-types" />
        <Resource name="conference-types" />
        <Resource name="project-statuses" />
        <Resource name="patent-statuses" />
        <Resource name="scientific-production-statuses" />
        <Resource name="scientific-production-types" />
        <Resource name="enhancement-action-types" />

        <Resource name="technical-reports" {...technicalReports} />
        <Resource name="technical-report-groups" {...technicalReportGroups} />
        <Resource
          name="technical-report-activities"
          {...technicalReportActivities}
        />
        <Resource
          name="technical-report-deliverables"
          {...technicalReportDeliverables}
        />
        <Resource
          name="technical-report-deliverable-results"
          {...technicalReportDeliverableResults}
        />
        <Resource
          name="technical-report-collaborations"
          {...technicalReportCollaborations}
        />
        <Resource
          name="technical-report-conferences"
          {...technicalReportConferences}
        />
        <Resource
          name="technical-report-projects"
          {...technicalReportProjects}
        />
        <Resource name="technical-report-patents" {...technicalReportPatents} />
        <Resource
          name="technical-report-scientific-productions"
          {...technicalReportScientificProductions}
        />
        <Resource
          name="technical-report-enhancement-actions"
          {...technicalReportEnhancementActions}
        />

        <Resource name="technical-report-evals" {...technicalReportEvals} />
        <Resource
          name="technical-report-eval-replies"
          {...technicalReportEvalReplies}
        />
        <Resource
          name="technical-report-final-outputs"
          {...technicalReportFinalOutputs}
        />
        <Resource name="technical-report-final-output-types" />
        <Resource name="technical-report-replies" {...technicalReportReplies} />
        <Resource name="monitoring" {...monitoring} />
        <Resource name="monitoring-experts" {...monitoringExperts} />
        <Resource name="monitoring-experts/assignment-statuses" />

        <Resource name="users" {...users} />
        <Resource name="user-profile-logs" />
        <Resource name="language-messages" {...languageMessages} />
        <Resource name="eval-items" />
        <Resource name="eval-judgments" />
        <Resource name="resources" />
        <Resource name="user-logs" />
        <Resource name="doc-files" {...docFiles} />
        <Resource name="doc-file-types" />

        <Resource name="workflow/transactions/executive-projects" />
        <Resource name="workflow/transactions/technical-reports" />
        <Resource name="workflow/transactions/technical-report-evals" />
        <Resource name="workflow/transactions/intermediate-technical-reports" />
      </Admin>
    </WorkflowProvider>
  );
};
export default App;
